<template>
  <div class="fixed-sidebar surface-section p-4 flex flex-column">
    <img :src="logo" alt="Logo" class="sidebar-logo w-full max-w-4rem my-3 mx-auto block">
    <h3 class="text-center font-semibold text-xs letter-spacing-2 text-gray-400 m-0 mb-1">IMAB FINANCE</h3>
    <div class="text-xs text-500 text-center">
      {{ firstName }} <span class="text-400">({{ userClaim }})</span>
    </div>
    <div class="menu-container flex flex-column flex-grow-1">
      <!-- Main Menu -->
      <div v-if="canAccessCommon" class="menu-section mt-2">
        <h4 class="text-xs font-bold text-gray-500 uppercase mb-2 ml-2">Main</h4>
        <ul class="menu list-none p-0 m-0 text-sm">
          <li v-for="item in mainMenu" :key="item.route" class="mb-2">
            <router-link :to="item.route" class="menu-link block px-2 py-2 text-gray-400 text-decoration-none border-round" active-class="active-link">
              <i :class="item.icon" class="mr-2 text-base"></i> {{ item.label }}
            </router-link>
          </li>
        </ul>
      </div>

      <!-- Payroll Menu -->
      <div v-if="visiblePayrollMenu.length" class="menu-section">
        <h4 class="text-xs font-bold text-gray-500 uppercase mb-2 ml-2">Payroll</h4>
        <ul class="menu list-none p-0 m-0 text-sm">
          <li v-for="item in visiblePayrollMenu" :key="item.route" class="mb-2">
            <router-link :to="item.route" class="menu-link block px-2 py-2 text-gray-400 text-decoration-none border-round" active-class="active-link">
              <i :class="item.icon" class="mr-2 text-base"></i> {{ item.label }}
            </router-link>
          </li>
        </ul>
      </div>

      <!-- Invoices Menu -->
      <div v-if="canAccessCommon" class="menu-section">
        <h4 class="text-xs font-bold text-gray-500 uppercase mb-2 ml-2">Invoices</h4>
        <ul class="menu list-none p-0 m-0 text-sm">
          <li v-for="item in visibleInvoiceMenu" :key="item.route" class="mb-2">
            <router-link :to="item.route" class="menu-link block px-2 py-2 text-gray-400 text-decoration-none border-round" active-class="active-link">
              <i :class="item.icon" class="mr-2 text-base"></i> {{ item.label }}
            </router-link>
          </li>
        </ul>
      </div>

      <!-- Expenses Menu -->
      <div v-if="canAccessFinancial" class="menu-section">
        <h4 class="text-xs font-bold text-gray-500 uppercase mb-2 ml-2">Expenses</h4>
        <ul class="menu list-none p-0 m-0 text-sm">
          <li v-for="item in expenseMenu" :key="item.route" class="mb-2">
            <router-link :to="item.route" class="menu-link block px-2 py-2 text-gray-400 text-decoration-none border-round" active-class="active-link">
              <i :class="item.icon" class="mr-2 text-base"></i> {{ item.label }}
            </router-link>
          </li>
        </ul>
      </div>

      <!-- Tenants Menu -->
      <div v-if="canAccessTenants" class="menu-section">
        <h4 class="text-xs font-bold text-gray-500 uppercase mb-2 ml-2">Tenants</h4>
        <ul class="menu list-none p-0 m-0 text-sm">
          <li v-for="item in tenantMenu" :key="item.route" class="mb-2">
            <router-link :to="item.route" class="menu-link block px-2 py-2 text-gray-400 text-decoration-none border-round" active-class="active-link">
              <i :class="item.icon" class="mr-2 text-base"></i> {{ item.label }}
            </router-link>
          </li>
        </ul>
      </div>

      <div class="mt-auto">
        <Button label="Logout" @click="logout" icon="pi pi-sign-out" severity="secondary" class="w-full" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import logo from '@/assets/orion-logo-white-bg.png';
import Button from 'primevue/button';

const store = useStore();
const router = useRouter();

const firstName = computed(() => {
  return store.getters.firstName;
});
const userClaim = computed(() => store.state.user?.customClaims?.claim || '');
const canAccessCommon = computed(() => ['superadmin', 'operations', 'accounts', 'finance'].includes(userClaim.value));
const canAccessFinancial = computed(() => ['superadmin', 'accounts', 'finance'].includes(userClaim.value));
const canAccessPayroll = computed(() => ['superadmin', 'operations', 'accounts'].includes(userClaim.value));
const canAccessAnnuals = computed(() => ['superadmin', 'accounts'].includes(userClaim.value));
const isFinance = computed(() => userClaim.value === 'finance');
const canAccessTenants = computed(() => ['superadmin', 'accounts'].includes(userClaim.value));

const mainMenu = ref([
  { label: 'Dashboard', icon: 'pi pi-home', route: '/' },
  { label: 'Employees', icon: 'pi pi-users', route: '/employees' },
]);

const payrollMenu = ref([
  { label: 'Batches', icon: 'pi pi-id-card', route: '/batches' },
  { label: 'Annuals', icon: 'pi pi-briefcase', route: '/annuals' },
  { label: 'Structure', icon: 'pi pi-percentage', route: '/structure' },
]);
const visiblePayrollMenu = computed(() => payrollMenu.value.filter((item) => {
  if (!canAccessPayroll.value) return false;
  return item.route !== '/annuals' || canAccessAnnuals.value;
}));

const invoiceMenu = ref([
  { label: 'Invoices', icon: 'pi pi-money-bill', route: '/invoices' },
  { label: 'Payment Profiles', icon: 'pi pi-credit-card', route: '/invoices/payment-profiles' },
  { label: 'Companies', icon: 'pi pi-building', route: '/companies' },
  { label: 'Contracts', icon: 'pi pi-address-book', route: '/contracts' }
]);
const visibleInvoiceMenu = computed(() => invoiceMenu.value.filter((item) => {
  if (item.route === '/invoices/payment-profiles' && !canAccessFinancial.value) return false;
  if (item.route === '/contracts' && isFinance.value) return false;
  return true;
}));

const expenseMenu = ref([
  { label: 'Monthly Expenses', icon: 'pi pi-wallet', route: '/expenses' },
  { label: 'Source Payments', icon: 'pi pi-arrow-down-left', route: '/expenses/source-payments' },
  { label: 'Accounts & Categories', icon: 'pi pi-cog', route: '/expenses/settings' }
]);

const tenantMenu = ref([
  { label: 'Tenants', icon: 'pi pi-building', route: '/tenants' },
]);

function logout() {
  store.dispatch('logout');
  router.push({ name: 'Login' });
}
</script>

<style scoped>
.fixed-sidebar {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.menu-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.menu-link {
  transition: background-color 0.3s ease, color 0.3s ease;
}

a {
  text-decoration: none;
}

.menu-link:hover {
  background-color: var(--highlight-bg);
  color: var(--highlight-text-color);
}

.active-link {
  background-color: var(--primary-900) !important;
  color: var(--highlight-text-color) !important;
  transition: background-color 0.3s ease, color 0.3s ease;
}
</style>
